import { Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { FieldValueLines } from "./FieldValueLines.tsx"

const { Text } = Typography

interface ComparisonTableColumn {
  /** Stable key, matched against each row's `values`/`cellNotes` map — not
   * necessarily "lead"/"proposal"/"contract"; the Contract Generate modal
   * uses whichever of "lead"/"proposal" is the row's actual baseline. */
  key: string
  label: string
}

interface ComparisonTableRow {
  key: string
  label: string
  /**
   * Drives the differing-value cell emphasis. A row with `hasDiff: false`
   * renders every present column's value in plain text, with no styling —
   * emphasis is a per-row signal (this repo's `FieldComparison.has_diff` is
   * Lead-relative and per-field, not per-cell), applied to every source
   * column present in that row.
   */
  hasDiff: boolean
  /** Pre-resolved display string per column key — already defaulted to
   * whatever "not set" placeholder the caller uses ("—" for Lead, "Not set"
   * for Proposal/Contract); this component never invents a fallback. A
   * missing entry renders as an empty cell. */
  values: Partial<Record<string, string>>
  /** Small muted note appended after the Field label, e.g. "(Lead only)"
   * for a field that can't be synced back into a document. */
  note?: string
  /** Small muted note rendered under one column's cell, keyed by column
   * key — e.g. "Locked (signed)" under a locked Contract value. */
  cellNotes?: Partial<Record<string, string>>
}

interface ComparisonTableProps {
  /** 2 to 4 source columns — the Field column is always added in front. */
  columns: ComparisonTableColumn[]
  /**
   * One row per canonical field the caller wants shown — render generically
   * off however many are present; never hardcode a field list here or at
   * a call site, so a newly added backend field (e.g. `sales_price`) shows
   * up with no client change.
   */
  rows: ComparisonTableRow[]
  /** Prefixes every `data-testid` this component renders:
   * `${testIdPrefix}-table`, `${testIdPrefix}-row-<key>`,
   * `${testIdPrefix}-cell-<key>-<columnKey>`. */
  testIdPrefix: string
  fieldColumnLabel?: string
}

/**
 * The Lead/Proposal/Contract field-comparison table shared by the three
 * Generate/Confirm-time modals (Project creation, Proposal Generate,
 * Contract Generate). A real HTML table — light-gray header, a thin bottom
 * border per row rather than a heavy grid, generous top-aligned padding so
 * a multi-line Scope/Payment Terms cell doesn't force a short neighboring
 * cell to center awkwardly, and natural wrapping (never truncation) so a
 * long value stays fully readable. No per-row "Recommended: ..." caption —
 * callers that have a recommendation concept (only the Project modal does)
 * surface it once, outside this table, as a single decision.
 */
function ComparisonTable({
  columns,
  rows,
  testIdPrefix,
  fieldColumnLabel
}: ComparisonTableProps) {
  const { t } = useTranslation()
  const resolvedFieldColumnLabel =
    fieldColumnLabel ?? t("documentSync.fieldColumn")
  const fieldColumnPercent = columns.length >= 3 ? 22 : 28
  const sourceColumnPercent = (100 - fieldColumnPercent) / columns.length

  return (
    <div className="overflow-x-auto border border-gray-200 rounded-md">
      <table
        className="w-full text-sm border-collapse"
        data-testid={`${testIdPrefix}-table`}
      >
        <colgroup>
          <col style={{ width: `${fieldColumnPercent}%` }} />
          {columns.map((column) => (
            <col key={column.key} style={{ width: `${sourceColumnPercent}%` }} />
          ))}
        </colgroup>
        <thead>
          <tr className="bg-gray-50 text-left">
            <th className="py-3 px-4 font-medium">{resolvedFieldColumnLabel}</th>
            {columns.map((column) => (
              <th key={column.key} className="py-3 px-4 font-medium">
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.key}
              className="border-b border-gray-200 last:border-b-0"
              data-testid={`${testIdPrefix}-row-${row.key}`}
              data-diff={row.hasDiff ? "true" : "false"}
            >
              <td className="py-3 px-4 align-top">
                <Text strong>{row.label}</Text>
                {row.note && (
                  <Text type="secondary" className="text-xs">
                    {" "}
                    {row.note}
                  </Text>
                )}
              </td>
              {columns.map((column) => {
                const value = row.values[column.key] ?? ""
                const cellNote = row.cellNotes?.[column.key]

                return (
                  <td
                    key={column.key}
                    data-testid={`${testIdPrefix}-cell-${row.key}-${column.key}`}
                    data-diff={row.hasDiff ? "true" : "false"}
                    className={
                      "py-3 px-4 align-top break-words" +
                      (row.hasDiff
                        ? " bg-amber-50 border-l-2 border-amber-300"
                        : "")
                    }
                  >
                    <FieldValueLines
                      value={value}
                      fieldKey={row.key}
                      variant="bulleted"
                    />
                    {cellNote && (
                      <Text type="secondary" className="text-xs block mt-1">
                        {cellNote}
                      </Text>
                    )}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export { ComparisonTable, type ComparisonTableColumn, type ComparisonTableRow }
